// src/pages/attorney/ClientPortalPage.tsx
// BP-10-04: Client (family) portal view of their legal case

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Briefcase,
  Calendar,
  FileText,
  MessageSquare,
  Phone,
  Mail,
  Clock,
  CheckCircle,
  AlertCircle,
  Send,
  User,
} from 'lucide-react';
import { useClientPortal, useSharedDocuments } from '../hooks/useAttorney';
import { ClientPortalNav, SharedDocumentsList } from '../components';
import { CaseStatusBadge } from '../components/CaseStatusBadge';
import { formatCurrency } from '../types/attorney';

export const ClientPortalPage: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'documents' | 'messages'>('overview');
  const [newMessage, setNewMessage] = useState('');

  const { caseView, isLoading, messages, sendMessage, isSending } = useClientPortal(caseId!);
  const { documents } = useSharedDocuments(caseId!);

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      sendMessage({ content: newMessage });
      setNewMessage('');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage"></div>
      </div>
    );
  }

  if (!caseView) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="w-12 h-12 text-warm-gray mb-4" />
        <p className="text-warm-gray">Case not found or access denied</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-xl border border-warm-gray/10 p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Briefcase className="w-6 h-6 text-sage" />
              <h1 className="text-2xl font-display font-semibold text-navy">
                Your Case
              </h1>
              <CaseStatusBadge status={caseView.status} />
            </div>
            <p className="text-warm-gray">
              Case #{caseView.case_number} • Filed {new Date(caseView.filed_date).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Attorney Contact */}
        <div className="flex items-center gap-6 p-4 bg-cream rounded-lg">
          <div className="flex items-center gap-3">
            {caseView.attorney?.photo_url ? (
              <img
                src={caseView.attorney.photo_url}
                alt={caseView.attorney.name}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-sage/20 flex items-center justify-center">
                <User className="w-6 h-6 text-sage" />
              </div>
            )}
            <div>
              <p className="font-medium text-navy">{caseView.attorney?.name}</p>
              <p className="text-sm text-warm-gray">{caseView.attorney?.firm_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 ml-auto">
            {caseView.attorney?.phone && (
              <a
                href={`tel:${caseView.attorney.phone}`}
                className="flex items-center gap-2 px-4 py-2 text-sm text-navy hover:bg-cream-dark rounded-lg transition-colors"
              >
                <Phone className="w-4 h-4" />
                {caseView.attorney.phone}
              </a>
            )}
            {caseView.attorney?.email && (
              <a
                href={`mailto:${caseView.attorney.email}`}
                className="flex items-center gap-2 px-4 py-2 text-sm text-navy hover:bg-cream-dark rounded-lg transition-colors"
              >
                <Mail className="w-4 h-4" />
                Email
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <ClientPortalNav activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Content */}
      <div className="mt-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-2 gap-6">
            {/* Case Summary */}
            <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
              <h3 className="font-medium text-navy mb-4">Case Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-warm-gray">Case Type</span>
                  <span className="text-navy">{caseView.case_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-warm-gray">Court</span>
                  <span className="text-navy">{caseView.court || 'TBD'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-warm-gray">Incident Date</span>
                  <span className="text-navy">
                    {caseView.incident_date
                      ? new Date(caseView.incident_date).toLocaleDateString()
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-warm-gray">Filed Date</span>
                  <span className="text-navy">
                    {caseView.filed_date
                      ? new Date(caseView.filed_date).toLocaleDateString()
                      : 'Not yet filed'}
                  </span>
                </div>
              </div>
            </div>

            {/* Upcoming Events */}
            <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
              <h3 className="font-medium text-navy mb-4">Upcoming Events</h3>
              {caseView.upcoming_events?.length > 0 ? (
                <div className="space-y-3">
                  {caseView.upcoming_events.slice(0, 3).map((event: any) => (
                    <div
                      key={event.id}
                      className="flex items-start gap-3 p-3 bg-cream rounded-lg"
                    >
                      <Calendar className="w-4 h-4 text-sage mt-0.5" />
                      <div>
                        <p className="font-medium text-navy text-sm">{event.title}</p>
                        <p className="text-xs text-warm-gray">
                          {new Date(event.event_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-warm-gray text-sm">No upcoming events scheduled</p>
              )}
            </div>

            {/* Recent Updates */}
            <div className="col-span-2 bg-white rounded-xl border border-warm-gray/10 p-6">
              <h3 className="font-medium text-navy mb-4">Recent Updates</h3>
              {caseView.recent_events?.length > 0 ? (
                <div className="space-y-4">
                  {caseView.recent_events.slice(0, 5).map((event: any) => (
                    <div
                      key={event.id}
                      className="flex items-start gap-4 pb-4 border-b border-warm-gray/10 last:border-0 last:pb-0"
                    >
                      <div className="w-8 h-8 rounded-full bg-sage/10 flex items-center justify-center flex-shrink-0">
                        {event.completed_at ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <Clock className="w-4 h-4 text-sage" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-navy">{event.title}</p>
                        {event.description && (
                          <p className="text-sm text-warm-gray mt-1">{event.description}</p>
                        )}
                        <p className="text-xs text-warm-gray mt-2">
                          {new Date(event.event_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-warm-gray text-sm">No recent updates</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
            <h3 className="font-medium text-navy mb-6">Case Timeline</h3>
            {caseView.visible_events?.length > 0 ? (
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-px bg-warm-gray/20" />
                <div className="space-y-6">
                  {caseView.visible_events.map((event: any, index: number) => (
                    <div key={event.id} className="relative pl-10">
                      <div
                        className={`absolute left-2 w-4 h-4 rounded-full border-2 ${
                          event.completed_at
                            ? 'bg-green-500 border-green-500'
                            : 'bg-white border-sage'
                        }`}
                      />
                      <div className="bg-cream rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <span className="text-xs text-warm-gray uppercase tracking-wide">
                              {event.event_type}
                            </span>
                            <h4 className="font-medium text-navy">{event.title}</h4>
                          </div>
                          <span className="text-sm text-warm-gray">
                            {new Date(event.event_date).toLocaleDateString()}
                          </span>
                        </div>
                        {event.description && (
                          <p className="text-sm text-warm-gray mt-2">{event.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-warm-gray text-center py-8">No timeline events to display</p>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
            <h3 className="font-medium text-navy mb-4">Shared Documents</h3>
            <SharedDocumentsList documents={documents || []} caseId={caseId!} />
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
            <div className="p-4 border-b border-warm-gray/10">
              <h3 className="font-medium text-navy">Messages with Your Attorney</h3>
            </div>

            {/* Messages List */}
            <div className="h-96 overflow-y-auto p-4 space-y-4">
              {messages?.length > 0 ? (
                messages.map((message: any) => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.is_from_attorney ? 'justify-start' : 'justify-end'
                    }`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg p-3 ${
                        message.is_from_attorney
                          ? 'bg-cream'
                          : 'bg-sage text-white'
                      }`}
                    >
                      <p className="text-sm">{message.content}</p>
                      <p
                        className={`text-xs mt-1 ${
                          message.is_from_attorney ? 'text-warm-gray' : 'text-white/70'
                        }`}
                      >
                        {new Date(message.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-warm-gray">
                  <MessageSquare className="w-12 h-12 mb-2 opacity-50" />
                  <p>No messages yet</p>
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-warm-gray/10">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 border border-warm-gray/20 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || isSending}
                  className="flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  Send
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientPortalPage;


